/* eslint-disable react-hooks/rules-of-hooks */
import { useTranslation } from 'react-i18next';
import { formatter } from '@repo/shared';
import type { ColumnDef } from '@tanstack/react-table';
import { Download, EllipsisVertical } from 'lucide-react';

import type { FilesQueryResult } from '@/api/generated';
import { ColumnHeader } from '@/components/blocks/data-table/column-header';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import useDownload from '@/hooks/use-download';
import { useFileDownload } from '@/hooks/use-file-download';

export const columns: Array<ColumnDef<FilesQueryResult[number]>> = [
  {
    accessorKey: 'name',
    header: ({ column }) => {
      const { t } = useTranslation();
      return (
        <ColumnHeader
          column={column}
          title={t('datasets.expansion.name')}
        />
      );
    },
  },
  {
    accessorKey: 'type',
    header: ({ column }) => {
      const { t } = useTranslation();
      return (
        <ColumnHeader
          column={column}
          title={t('datasets.expansion.type')}
        />
      );
    },
    cell: ({ row }) => {
      const { t } = useTranslation();
      if (row.original.type === 'INPUT') {
        return t('datasets.expansion.input');
      }
      if (row.original.type === 'REFERENCE') {
        return t('datasets.expansion.reference');
      }
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      if (row.original.type === 'OUTPUT') {
        return t('datasets.expansion.output');
      }
      return t('datasets.expansion.unknown');
    },
  },
  {
    accessorKey: 'mimeType',
    header: ({ column }) => {
      const { t } = useTranslation();
      return (
        <ColumnHeader
          column={column}
          title={t('datasets.expansion.contentType')}
        />
      );
    },
    cell: ({ row, column }) => row.getValue(column.id),
  },
  {
    accessorKey: 'size',
    header: ({ column }) => {
      const { t } = useTranslation();
      return (
        <ColumnHeader
          column={column}
          title={t('datasets.expansion.size')}
        />
      );
    },
    cell: ({ row }) => formatter.bytes.format(row.original.size),
  },
  {
    accessorKey: 'createdAt',
    header: ({ column }) => {
      const { t } = useTranslation();
      return (
        <ColumnHeader
          column={column}
          title={t('datasets.expansion.createdAt')}
        />
      );
    },
    cell: ({ row }) =>
      formatter.date.dateTime(new Date(row.original.createdAt as string)),
  },
  {
    id: 'actions',
    header: () => null,
    cell: ({ row }) => {
      const { t } = useTranslation();
      const { downloadFile } = useFileDownload({
        mutationKey: row.original.id,
        path: `/api/files/${row.original.id}/download`,
      });
      const { download } = useDownload();
      return (
        <DropdownMenu>
          <DropdownMenuTrigger>
            <Button variant={'outline'}>
              <Button.Icon icon>
                <EllipsisVertical />
              </Button.Icon>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent>
            <DropdownMenuItem
              onClick={async () => {
                const { url, fileName } = await downloadFile();
                download({
                  url,
                  file: fileName,
                });
              }}>
              <Download />
              {t('datasets.expansion.download')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      );
    },
  },
];
